import { Spotlight, type SpotlightActionData, type SpotlightFilterFunction } from "@mantine/spotlight";
import Fuse from "fuse.js";
import { useMemo } from "react";
import { IconSearch } from "./icons";
import { utilities } from "./utility-registry";

// The navbar in one keystroke, mounted by the layout so the shortcut answers on every page.
export function UtilitySpotlight() {
  // The label is what the navbar calls it and the description is the headline its page is found by, since "Codec"
  // alone is a word for somebody already here. Choosing one loads its page, the way clicking the navbar's link does.
  const actions = useMemo<SpotlightActionData[]>(
    () =>
      utilities.map(({ path, label, title, Icon }) => ({
        id: path,
        label,
        description: title,
        leftSection: <Icon size="1.2rem" stroke={1.5} />,
        onClick: () => window.location.assign(path),
      })),
    [],
  );

  return (
    <Spotlight
      actions={actions}
      filter={filterUtilities}
      // `ctrl` rather than Mantine's `mod`, which is either of the two: Cmd+Space is macOS Spotlight's own and the
      // window server takes it before a page is handed anything, so `mod` would be claiming a key it can never be
      // given. Never the one CodeMirror is holding either — `triggerOnContentEditable` is left false, so an editor
      // with the caret in it keeps the key for its own completion and the search is a Tab or an Escape away.
      // Nothing else on the page has a claim on it, so no tag is ignored and typing into a field is no bar to it.
      shortcut="ctrl + space"
      tagsToIgnore={[]}
      searchProps={{ placeholder: "Search utilities", leftSection: <IconSearch size="1.2rem" stroke={1.5} /> }}
      nothingFound="No utility goes by that."
      scrollable
    />
  );
}

// The index is the registry's own and is built once with it: what a utility is called and the words it is searched
// for cannot change while the page is up.
const fuse = new Fuse(
  utilities.map(({ path, label, keywords }) => ({ path, label, keywords })),
  {
    keys: [{ name: "label", weight: 2 }, { name: "keywords", weight: 1 }],
    // A match anywhere in a keyword counts, and a name need not be typed the way it is spelled: "bcrypt" and "colur"
    // reach /hasher and /colour, which the substring match Mantine filters with answers nothing for. Any looser and a
    // page starts being reached by the words of another — /javascript answered for "bcrypt" at 0.4.
    threshold: 0.3,
    ignoreLocation: true,
  },
);

// Every keyword whole, and every word of one on its own, since "bcrypt generator" is the entry somebody reaches by
// typing "bcrypt". Split on whitespace alone: "aes-gcm" and "sha-3" are one word each and are typed as they are.
const SPELLED = new Map<string, Set<string>>(
  utilities.map(({ path, keywords }) => [path, new Set(keywords.flatMap((word) => [word, ...word.split(/\s+/)]))]),
);

// Ranked by the search and then read back off the actions by path, so the list is what matched and its best is first.
// A word typed out in full that a page lists among its own goes above a page that only shares some of its letters:
// "bcrypt" is /hasher's word, and a fuzzy match otherwise scores it against /cryptography for the six in the middle.
export const filterUtilities: SpotlightFilterFunction = (query, actions) => {
  const search = query.trim();
  if (!search) return actions;

  const byPath = new Map(actions.flatMap((action) => "id" in action ? [[action.id, action] as const] : []));
  const ranked = fuse.search(search).map(({ item }) => item.path);
  const spelled = (path: string) => SPELLED.get(path)?.has(search.toLowerCase()) ?? false;
  return [...ranked.filter(spelled), ...ranked.filter((path) => !spelled(path))]
    .flatMap((path) => byPath.get(path) ?? []);
};
